import type { ProjectTileRoccoFragment } from 'generated/graphql';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import { getProjectTileThumbnail } from '~/utils/get-tile-thumbnail-for-project';
/**
 * Maps a project fragment to a tile view model for display purposes.
 *
 * @param {ProjectTileRoccoFragment} item - The project fragment to be mapped. This includes details such as the project's ID, title, link, and associated images.
 * @param photoTile - new tile aspect 440/320
 * @param {'featured' | 'default'} - Determines which image to display. 'featured' uses a high-resolution image if available, otherwise defaults to the standard image.
 * @returns {ProjectTileViewModel} The mapped project tile view model, including project ID, title, link, images, professional details, and award status.
 */
export const mapProjectToTileViewModel = (
    item: ProjectTileRoccoFragment,
    {
        appDisplayMode = 'desktop',
    }: {
        imageToDisplay?: 'featured' | 'default';
        appDisplayMode?: DisplayMode;
    } = {},
    photoTile?: string
): ProjectTileViewModel => {
    const thumbnailSrc = getProjectTileThumbnail(
        item,
        appDisplayMode === 'mobile'
    );

    return {
        id: item.ID,
        title: item.Title,
        link: item.Link,
        images: [
            {
                src: photoTile || thumbnailSrc,
                alt: item.Title,
                multiplied: item.LibraryThumb?.IsMultiplied,
            },
        ],
        professional: {
            name: item.Professional?.Title || '',
            id: item.Professional?.ID || 0,
            link: item.Professional?.Link || '',
        },
        video: [],
        hasAwards: !!item.Awards?.edges?.length,
        badge: item.Awards?.edges?.length > 0 ? 'Award winning project' : null,
    };
};
